import React from 'react';
import { 
  BookOpen, 
  Rocket, 
  MessageCircle, 
  Brain, 
  FileText, 
  Folders,
  Search as SearchIcon,
  Sparkles,
  CreditCard,
  Lightbulb,
  HelpCircle
} from 'lucide-react';

interface DocsSidebarProps {
  activeSection?: string;
  onSectionClick?: (sectionId: string) => void;
  isMobileOpen?: boolean;
  onMobileClose?: () => void;
}

interface NavSection {
  id: string;
  title: string;
  icon: React.ReactNode;
  subsections?: { id: string; title: string }[];
}

const navSections: NavSection[] = [
  {
    id: 'getting-started',
    title: '🚀 Getting Started',
    icon: <Rocket className="w-4 h-4" />,
    subsections: [
      { id: 'welcome', title: 'Welcome to CommsGenie' },
      { id: 'quick-start', title: 'Quick Start Guide' },
      { id: 'understanding-dashboard', title: 'Understanding Your Dashboard' },
    ],
  },
  {
    id: 'core-features',
    title: '⚙️ Core Features',
    icon: <Sparkles className="w-4 h-4" />,
    subsections: [
      { id: 'proceedings', title: 'Proceedings' },
      { id: 'chat-modes', title: 'Chat Modes' },
      { id: 'search-discovery', title: 'Document Search & Discovery' },
    ],
  },
  {
    id: 'advanced-features',
    title: '🔬 Advanced Features',
    icon: <Brain className="w-4 h-4" />,
    subsections: [
      { id: 'comment-summary', title: 'Comment Summary Generation' },
      { id: 'export', title: 'Export & Integration' },
    ],
  },
  {
    id: 'account-billing',
    title: '💳 Account & Billing',
    icon: <CreditCard className="w-4 h-4" />,
    subsections: [
      { id: 'usage-tiers', title: 'Understanding Usage Tiers' },
      { id: 'managing-subscription', title: 'Managing Your Subscription' },
    ],
  },
  {
    id: 'best-practices',
    title: '✨ Best Practices',
    icon: <Lightbulb className="w-4 h-4" />,
    subsections: [
      { id: 'effective-questions', title: 'Writing Effective Questions' },
      { id: 'research-workflows', title: 'Regulatory Research Workflows' },
      { id: 'tips-tricks', title: 'Tips & Tricks' },
    ],
  },
  {
    id: 'support',
    title: '❓ Support',
    icon: <HelpCircle className="w-4 h-4" />,
    subsections: [
      { id: 'faq', title: 'Frequently Asked Questions' },
      { id: 'getting-help', title: 'Getting Help' },
    ],
  },
];

export const DocsSidebar: React.FC<DocsSidebarProps> = ({
  activeSection = 'welcome',
  onSectionClick,
  isMobileOpen = false,
  onMobileClose,
}) => {
  const handleSectionClick = (sectionId: string) => {
    onSectionClick?.(sectionId);
    onMobileClose?.();
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div aria-hidden="true"
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onMobileClose}
        />
      )}

      {/* Sidebar */}
      <div
        className={`
          fixed lg:sticky top-0 left-0 h-screen lg:max-h-[calc(100vh-64px)]
          w-72 bg-white border-r border-gray-200
          transform transition-transform duration-300 ease-in-out
          lg:transform-none z-50 lg:z-0
          overflow-y-auto
          ${isMobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="p-6">
          <div className="flex items-center space-x-2 mb-6">
            <BookOpen className="w-6 h-6 text-green-600" />
            <h2 className="text-xl font-bold text-gray-900">📖 Documentation</h2>
          </div>

          <nav className="space-y-1">
            {navSections.map((section) => (
              <div key={section.id} className="mb-4">
                <h3 className="flex items-center space-x-2 px-3 py-2 text-sm font-bold text-gray-900 mt-4 first:mt-0">
                  <span>{section.title}</span>
                </h3>
                {section.subsections && (
                  <div className="space-y-1 mt-1">
                    {section.subsections.map((subsection) => (
                      <button
                        key={subsection.id}
                        onClick={() => handleSectionClick(subsection.id)}
                        className={`
                          w-full text-left px-3 py-2 text-sm rounded-md
                          transition-colors duration-200
                          ${
                            activeSection === subsection.id
                              ? 'bg-green-50 text-green-700 font-medium'
                              : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          }
                        `}
                      >
                        {subsection.title}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>
      </div>
    </>
  );
};

